import axios from 'axios';
import {
  getToolboxToken, getNcToken,
  stripMoney, parseAmount, fmtDateSlash, computeExpirationDate,
  type PremFiInsuredData,
} from './premiumFinanceScanShared';

/* NowCerts submission chain — mirrors createNewInsured/createNewPolicy/
 * insertPolicyEndorsementFee/updatePfScanHistory in the legacy
 * PFSOcrScan.tsx, so the three practical intake combinations
 * (existing client + existing policy | existing client + new policy |
 * new client + new policy) push the same request/response shapes to NowCerts. */

export interface SubmitCtx {
  insured: PremFiInsuredData;
  createNewClient: boolean;
  createNewPolicyFlag: boolean;
  insuredId: string;
  existingPolicyId: string;
}

export interface SubmitStepResult { label: string; success: boolean; message: string; }

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToolboxToken()}` } });

async function createNewInsured(insured: PremFiInsuredData): Promise<{ ok: boolean; insuredId: string; ncUrl: string; result: SubmitStepResult }> {
  const payload = {
    commercialName: insured.insuredFirstName,
    firstName: '', lastName: '', dba: '',
    addressLine1: insured.addressLine1, addressLine2: '',
    state: insured.state, city: insured.city, zipCode: insured.zipCode,
    eMail: '', cellPhone: '', fein: '', insuredType: 0,
  };
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/insertinsured?nowCertsToken=${getNcToken()}`, payload, authHeaders());
    return {
      ok: true, insuredId: res.data?.database_id || '', ncUrl: res.data?.newInsuredLunchUrl || '',
      result: { label: 'Insured', success: true, message: 'Insured has been created successfully in NowCerts' },
    };
  } catch (err: any) {
    const message = err?.response?.status === 400
      ? (err.response.data?.errors || 'Validation error')
      : 'Please correct NowCerts username and password.';
    return { ok: false, insuredId: '', ncUrl: '', result: { label: 'Insured', success: false, message } };
  }
}

async function createNewPolicy(insuredDatabaseId: string, insured: PremFiInsuredData): Promise<{ ok: boolean; policyId: string; result: SubmitStepResult }> {
  const payload = {
    insuredDatabaseId,
    firstName: '', lastName: '',
    number: insured.policyNumber,
    effectiveDate: fmtDateSlash(insured.effectiveDate),
    expirationDate: computeExpirationDate(insured.effectiveDate, insured.term),
    bindDate: '', businessType: 0, billingType: 0, mgaName: '',
    carrierName: insured.carrierName,
    premium: stripMoney(insured.premium),
    lineOfBusinessName: insured.lineOfBusiness,
    agencyFee: parseAmount(insured.otherFees),
    taxes: parseAmount(insured.otherTaxes),
    isPolicyRenewal: false, parentPolicyId: '',
  };
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/addpolicy?nowCertsToken=${getNcToken()}`, payload, authHeaders());
    const id = res.data?.policiesOrQuotes?.[0]?.policyOrQuoteId || '';
    return { ok: true, policyId: id, result: { label: 'Policy', success: true, message: 'Policy has been created successfully in NowCerts' } };
  } catch {
    return { ok: false, policyId: '', result: { label: 'Policy', success: false, message: 'Unknown error while adding new policy' } };
  }
}

function buildEndorsementFeePayload(policyId: string, insured: PremFiInsuredData): any[] {
  const nowIso = new Date().toJSON();
  const entries: any[] = [{
    order: 0,
    policyId,
    amount: parseAmount(insured.premium),
    date: nowIso,
    premiumType: 0,
    billingType: 4,
    type: 0,
    followUpDate: nowIso,
    receivables: [{ type: 0, order: 0, amountValue: parseAmount(insured.downPayment), amountType: 1, dueDate: nowIso }],
    payables:    [{ type: 0, order: 0, amountValue: parseAmount(insured.downPayment), amountType: 1, dueDate: nowIso }],
    financings:  [{ type: 0, order: 0, amountValue: parseAmount(insured.financedAmount), amountType: 1, dueDate: nowIso }],
    agencyCommissions: [{ order: 0, commissionType: 0, commissionValue: 10.0, type: 0, dueDate: nowIso }],
  }];
  const otherFees = parseAmount(insured.otherFees);
  if (otherFees > 0) entries.push({ order: 0, policyId, amount: otherFees, date: nowIso, premiumType: 1, type: 3, followUpDate: nowIso });
  const otherTaxes = parseAmount(insured.otherTaxes);
  if (otherTaxes > 0) entries.push({ order: 0, policyId, amount: otherTaxes, date: nowIso, premiumType: 1, type: 4, followUpDate: nowIso });
  return entries;
}

async function insertPolicyEndorsementFee(policyId: string, insured: PremFiInsuredData): Promise<{ ok: boolean; response: any; requestBody: any[]; result: SubmitStepResult }> {
  const requestBody = buildEndorsementFeePayload(policyId, insured);
  try {
    const res = await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/insertpolicyendorsementfee?ncToken=${getNcToken()}`, requestBody, authHeaders());
    return { ok: true, response: res.data, requestBody, result: { label: 'Fees & Financing', success: true, message: 'Policy endorsement fee added successfully' } };
  } catch {
    return { ok: false, response: null, requestBody, result: { label: 'Fees & Financing', success: false, message: 'Unknown error while adding policy endorsement fee.' } };
  }
}

async function updatePfScanHistory(submissionId: string, requestJson: any, responseJson: any): Promise<void> {
  try {
    await axios.post(`${process.env.REACT_APP_NOWCERTSAPI}/api/NowCert/updatepfscan`, {
      SubmissionId: submissionId,
      NowcertsRequestJson: JSON.stringify(requestJson),
      NowcertsResponseJson: JSON.stringify(responseJson),
    }, authHeaders());
  } catch { /* matches legacy: only shown as a toast, doesn't block the flow */ }
}

export async function submitPremiumFinanceScanToNowCerts(
  ctx: SubmitCtx,
  onStep?: (step: SubmitStepResult) => void,
): Promise<{ results: SubmitStepResult[]; insuredId: string; policyId: string; ncUrl: string; createdNewClient: boolean }> {
  const results: SubmitStepResult[] = [];
  const emit = (step: SubmitStepResult) => { results.push(step); onStep?.(step); };

  let insuredId = ctx.insuredId;
  let policyId = ctx.existingPolicyId;
  let ncUrl = '';
  let createdNewClient = false;

  if (ctx.createNewClient) {
    const r = await createNewInsured(ctx.insured);
    emit(r.result);
    if (!r.ok) return { results, insuredId, policyId, ncUrl, createdNewClient };
    insuredId = r.insuredId;
    ncUrl = r.ncUrl;
    createdNewClient = true;
  }

  if (ctx.createNewPolicyFlag) {
    const r = await createNewPolicy(insuredId, ctx.insured);
    emit(r.result);
    if (!r.ok) return { results, insuredId, policyId, ncUrl, createdNewClient };
    policyId = r.policyId;
  }

  const feeResult = await insertPolicyEndorsementFee(policyId, ctx.insured);
  emit(feeResult.result);
  if (feeResult.ok) {
    await updatePfScanHistory(ctx.insured.submissionId, feeResult.requestBody, feeResult.response);
  }

  return { results, insuredId, policyId, ncUrl, createdNewClient };
}
